<template>
  <div class="language-fab-container" ref="fabRef">
    <!-- Language options menu -->
    <transition name="fade">
      <div v-if="isOpen" class="language-options">
        <button
          v-for="(label, key) in languages"
          :key="key"
          class="language-option"
          :class="{ active: currentLocale === key }"
          @click="changeLanguage(key)"
        >
          <span class="language-label">{{ label }}</span>
          <span class="language-sublabel">{{ getEnglishName(key) }}</span>
          <i v-if="currentLocale === key" class="bi bi-check-lg check-icon"></i>
        </button>
      </div>
    </transition>

    <!-- Main FAB button -->
    <button
      class="language-fab"
      :class="{ active: isOpen }"
      @click="toggleMenu"
      :aria-label="$t('common.changeLanguage') || 'Change Language'"
    >
      <i class="bi bi-translate"></i>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLocale } from '../../plugins/i18n';
import { colors } from '../../../consts/colors.js';

const { locale, t: $t } = useI18n();

const languages = {
  ja: '日本語',
  en: 'English',
  vi: 'Tiếng Việt',
  th: 'ภาษาไทย'
};

const englishNames = {
  ja: 'Japanese',
  en: 'English',
  vi: 'Vietnamese',
  th: 'Thai'
};

const isOpen = ref(false);
const fabRef = ref(null);

const currentLocale = computed(() => locale.value);

// Color constants for v-bind in CSS
const c = colors;

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const changeLanguage = (lang) => {
  setLocale(lang);
  isOpen.value = false;
};

const getEnglishName = (key) => {
  return englishNames[key];
};

// Close menu when clicking outside
const handleClickOutside = (event) => {
  if (fabRef.value && !fabRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.language-fab-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.language-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: v-bind('c.btnBgColorPrimary');
  color: v-bind('c.white');
  border: none;
  box-shadow: 0 4px 12px v-bind('c.shadowPrimary');
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.language-fab:hover {
  transform: scale(1.1);
  background-color: v-bind('c.blue[600]');
  box-shadow: 0 6px 16px v-bind('c.shadowPrimaryHover');
}

.language-fab:active {
  transform: scale(0.95);
}

.language-options {
  position: absolute;
  bottom: 72px;
  right: 0;
  background: v-bind('c.white');
  border-radius: 12px;
  box-shadow: 0 8px 24px v-bind('c.shadowMedium');
  overflow: hidden;
  min-width: 200px;
}

.language-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 16px;
  width: 100%;
  border: none;
  background: v-bind('c.white');
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  text-align: left;
}

.language-option:hover {
  background-color: v-bind('c.bgHoverLight');
}

.language-option.active {
  background-color: v-bind('c.bgActive');
}

.language-option:not(:last-child) {
  border-bottom: 1px solid v-bind('c.borderMedium');
}

.language-label {
  font-size: 15px;
  font-weight: 600;
  color: v-bind('c.textPrimary');
  margin-bottom: 2px;
}

.language-option.active .language-label {
  color: v-bind('c.textActive');
}

.language-sublabel {
  font-size: 12px;
  color: v-bind('c.textSecondary');
}

.language-option.active .language-sublabel {
  color: v-bind('c.textActive');
}

.check-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: v-bind('c.textActive');
  font-size: 18px;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .language-fab-container {
    bottom: 16px;
    right: 16px;
  }

  .language-fab {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .language-options {
    bottom: 64px;
    min-width: 180px;
  }
}
</style>
